import en from '../assets/i18n/eng.json';
import hr from '../assets/i18n/hr.json';

type SupportedLanguage = 'en' | 'hr';

type LocaleConfig = {
  navbar: {
    routes: Record<string, string>;
  };
  solarEquipment: {
    urlCategories: Record<string, string>;
  };
  chargersEquipment: {
    urlCategories: Record<string, string>;
    urlCategoriesDropdown: Record<string, string>;
  };
};

type RouteDefinition = {
  routeKey: string;
  segmentsByLanguage: Record<SupportedLanguage, string[]>;
  allowDynamicTail?: boolean;
};

const localeConfig: Record<SupportedLanguage, LocaleConfig> = {
  en,
  hr,
};

const supportedLanguages: SupportedLanguage[] = ['en', 'hr'];

function normalizeKey(key: string) {
  return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function splitPath(value: string) {
  return value.split('/').filter(Boolean);
}

function joinPathSegments(segments: string[]) {
  return segments.length ? `/${segments.join('/')}` : '';
}

function buildDefinitionMap() {
  const definitions = new Map<string, RouteDefinition>();

  function addDefinition(keys: string[], getSegments: (language: SupportedLanguage) => string[], allowDynamicTail = false) {
    const routeKey = keys.map(normalizeKey).join(':');

    if (definitions.has(routeKey)) {
      const existingDefinition = definitions.get(routeKey);

      if (existingDefinition && allowDynamicTail) {
        existingDefinition.allowDynamicTail = true;
      }

      return;
    }

    const segmentsByLanguage = supportedLanguages.reduce((acc, language) => {
      acc[language] = getSegments(language);
      return acc;
    }, {} as Record<SupportedLanguage, string[]>);

    if (supportedLanguages.some((language) => segmentsByLanguage[language].length === 0)) {
      return;
    }

    definitions.set(routeKey, {
      routeKey,
      segmentsByLanguage,
      allowDynamicTail,
    });
  }

  const navbarRouteKeys = new Set<string>();

  supportedLanguages.forEach((language) => {
    Object.keys(localeConfig[language].navbar.routes).forEach((key) => {
      navbarRouteKeys.add(key);
    });
  });

  navbarRouteKeys.forEach((key) => {
    addDefinition([key], (language) => splitPath(localeConfig[language].navbar.routes[key] || ''), key === 'equipment' || key === 'search');
  });

  const solarCategoryKeys = new Set<string>();

  supportedLanguages.forEach((language) => {
    Object.keys(localeConfig[language].solarEquipment.urlCategories).forEach((key) => {
      solarCategoryKeys.add(key);
    });
  });

  solarCategoryKeys.forEach((key) => {
    addDefinition(['equipment', key], (language) => {
      const baseSegments = splitPath(localeConfig[language].navbar.routes.equipment || '');
      const categorySegments = splitPath(localeConfig[language].solarEquipment.urlCategories[key] || '');
      return [...baseSegments, ...categorySegments];
    });
  });

  const chargerCategoryKeys = new Set<string>();

  supportedLanguages.forEach((language) => {
    Object.keys(localeConfig[language].chargersEquipment.urlCategories).forEach((key) => {
      chargerCategoryKeys.add(key);
    });
  });

  chargerCategoryKeys.forEach((key) => {
    addDefinition(['chargers', key], (language) => {
      const baseSegments = splitPath(localeConfig[language].navbar.routes.chargers || '');
      const categorySegments = splitPath(localeConfig[language].chargersEquipment.urlCategories[key] || '');
      return [...baseSegments, ...categorySegments];
    });
  });

  const chargerDropdownKeys = new Set<string>();

  supportedLanguages.forEach((language) => {
    Object.keys(localeConfig[language].chargersEquipment.urlCategoriesDropdown).forEach((key) => {
      chargerDropdownKeys.add(key);
    });
  });

  chargerDropdownKeys.forEach((key) => {
    addDefinition(['chargers', key], (language) => {
      const baseSegments = splitPath(localeConfig[language].navbar.routes.chargers || '');
      const dropdownSegments = splitPath(localeConfig[language].chargersEquipment.urlCategoriesDropdown[key] || '');
      return [...baseSegments, ...dropdownSegments];
    });
  });

  addDefinition(['chargers', 'evChargers'], (language) => {
    const baseSegments = splitPath(localeConfig[language].navbar.routes.chargers || '');
    const childSegments = splitPath(localeConfig[language].navbar.routes.evChargers || '');
    return [...baseSegments, ...childSegments];
  });

  addDefinition(['chargers', 'chargerAccessories'], (language) => {
    const baseSegments = splitPath(localeConfig[language].navbar.routes.chargers || '');
    const childSegments = splitPath(
      localeConfig[language].navbar.routes.chargerAccessories ||
      localeConfig[language].navbar.routes['charger-accessories'] ||
      ''
    );
    return [...baseSegments, ...childSegments];
  });

  return Array.from(definitions.values()).sort((left, right) => right.segmentsByLanguage.en.length - left.segmentsByLanguage.en.length);
}

const routeDefinitions = buildDefinitionMap();

function getHomePath(targetLanguage: SupportedLanguage) {
  return `/${targetLanguage}${localeConfig[targetLanguage].navbar.routes.home}`;
}

function buildUrl(pathname: string, search = '', hash = '') {
  return `${pathname}${search}${hash}`;
}

export function translateLocalizedPath(pathname: string, targetLanguage: SupportedLanguage, search = '', hash = '') {
  const segments = pathname.split('/').filter(Boolean);
  const currentLanguage = segments[0] as SupportedLanguage | undefined;

  if (!currentLanguage || !supportedLanguages.includes(currentLanguage)) {
    return buildUrl(getHomePath(targetLanguage), search, hash);
  }

  if (currentLanguage === targetLanguage) {
    return buildUrl(pathname, search, hash);
  }

  const pathWithoutLanguage = segments.slice(1);

  if (pathWithoutLanguage.length === 0) {
    return buildUrl(getHomePath(targetLanguage), search, hash);
  }

  for (const definition of routeDefinitions) {
    const localizedSegments = definition.segmentsByLanguage[currentLanguage];
    const matchesDefinition = localizedSegments.every((segment, index) => pathWithoutLanguage[index] === segment);

    if (!matchesDefinition) {
      continue;
    }

    const remainingSegments = pathWithoutLanguage.slice(localizedSegments.length);

    if (remainingSegments.length > 0 && !definition.allowDynamicTail) {
      continue;
    }

    const translatedSegments = definition.segmentsByLanguage[targetLanguage];
    const translatedPath = joinPathSegments([targetLanguage, ...translatedSegments, ...remainingSegments]);

    return buildUrl(translatedPath, search, hash);
  }

  return buildUrl(getHomePath(targetLanguage), search, hash);
}
